import { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { ToastVariants } from '@dotcom/crown-ui';
import { useShallow } from 'zustand/react/shallow';
import { useAnalyticsContext } from '../../../context/analytics-context';
import { hideLoadingScreen, setIsToasterOpen, useAppContext } from '../../../context/app-context';
import { useDataContext } from '../../../context/data-context/use-data-context';
import { useAddressBookAddresses } from '../../../features/address-book/hooks/use-address-book-addresses';
import { useAddressStore } from '../../../stores/address';
import {
  Address,
  AddressForm,
  AddressRecipientUpdate,
  AddressResponseData,
  AddressToSave,
  AddressTypeCode,
  ErrorType,
  QuickAddressSubmitData,
} from '../../../global-types/addresses';
import { EnvelopeType } from '../../../global-types/envelope';
import { ErrorResponse } from '../../../global-types/services';
import { useFeatureFlags, useSystemErrorHandling } from '../../../hooks';
import { useAddressNavigation } from '../../../hooks/use-address-navigation';
import { useIsOneToMany } from '../../../hooks/use-is-one-to-many';
import { saveAddress } from '../../../services';
import { addressOptions, updateAddress } from '../../../services/address';
import { getIsDigitalGreeting, helperSettingsConfig, isLoggedIn } from '../../../utils';
import { getAddressError, getAddressErrorType } from '../../../utils/address/get-address-error';
import { mapAddressesByType } from '../../../utils/address/map-address-by-type';
import { useLoadExistingProject } from '../../editor/hooks/use-load-existing-project';
import { HandleAddressResubmit } from '../address-types';
import { trackSubmittedNameOnEnvelope } from '../utils/track-submitted-name-on-envelope';
import { useEnvelopeAddresses } from './use-envelope-addresses';

type UseHandleSubmit = {
  openAddressConfirmationDialog: () => void;
  closeAddressConfirmationDialog: () => void;
  addAddressToValidate: (
    address: Address & {
      address_id?: string;
    },
    errorType: ErrorType,
    step: number,
    contactId: any,
  ) => void;
  envelope?: EnvelopeType | null;
};

export const useHandleSubmit = ({
  openAddressConfirmationDialog,
  closeAddressConfirmationDialog,
  addAddressToValidate,
  envelope,
}: UseHandleSubmit) => {
  // state
  const [isAddressLoading, setIsAddressLoading] = useState(false);

  // contexts
  const {
    initializedDataState: { data },
  } = useDataContext();
  const { isRecipientAddress, setAddressData, setIsRecipientAddress } = useAddressStore(
    useShallow((store) => ({
      isRecipientAddress: store.isRecipientAddress,
      setAddressData: store.setAddressData,
      setIsRecipientAddress: store.setIsRecipientAddress,
    })),
  );
  const { appDispatch } = useAppContext();
  const { trackClickNextToAddressSender, trackManuallyAddRecipientQuickAddress, trackNameOnEnvelopeUsage } =
    useAnalyticsContext();

  // hooks
  const { t } = useTranslation();
  const { loadExistingProject } = useLoadExistingProject();
  const { onSystemError } = useSystemErrorHandling();
  const { IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED, IS_ADDRESS_DISPLAY_NAME_ENABLED } = useFeatureFlags();
  const { navigateWithParams } = useAddressNavigation();
  const isOneToMany = useIsOneToMany();
  const queryClient = useQueryClient();
  const { saveAddressToAddressBook } = useAddressBookAddresses();
  const addressTypesToFill = useEnvelopeAddresses();

  // memoized constants
  const lastProjectLoaded = useMemo(() => sessionStorage.getItem('lastProjectLoaded'), []);
  const isDigitalGreeting = useMemo(() => getIsDigitalGreeting(), []);
  const projectId = useMemo(() => data?.project_id, [data?.project_id]);

  // ref to check if the project is loaded
  const isDataLoaded = useRef<boolean>(false);

  const trackSavedNameOnEnvelope = useCallback(
    (address: Pick<AddressForm, 'address_type_code' | 'display_name'>) => {
      if (isDigitalGreeting) return;
      trackSubmittedNameOnEnvelope(address, IS_ADDRESS_DISPLAY_NAME_ENABLED, trackNameOnEnvelopeUsage);
    },
    [IS_ADDRESS_DISPLAY_NAME_ENABLED, isDigitalGreeting, trackNameOnEnvelopeUsage],
  );

  // maps the address form to the AddressToSave type to be saved to the project
  const mapAddressToSave = (address: AddressForm | Address | AddressResponseData): AddressToSave => {
    return {
      address_line_1: address.address_line_1,
      address_line_2: address.address_line_2,
      address_type_code: address.address_type_code,
      city: address.city,
      company_name: address.company_name,
      country_code: address.country_code,
      first_name: address.first_name,
      last_name: address.last_name,
      display_name: address.display_name,
      skip_usps_validation: (address as Address).skip_usps_validation || false,
      state_code: address.state_code,
      zip: address.zip,
    };
  };

  // shows error toast from the app context state
  const showErrorToast: (error: string, title?: string) => void = useCallback(
    (error, title) => {
      setIsToasterOpen(appDispatch, {
        variant: ToastVariants.Error,
        title: title || t('addressView.failedToast'),
        children: error,
      });
    },
    [appDispatch, t],
  );

  // handles errors when saving or updating an address
  // adding the error to the validation state and hiding the loading screen
  const handleError: (
    error: ErrorResponse,
    address: Address | AddressResponseData,
    mappedAddress: AddressToSave | AddressRecipientUpdate,
  ) => void = useCallback(
    (error, address, mappedAddress) => {
      const addressError = getAddressError((error as ErrorResponse).data.errors);
      addAddressToValidate(
        {
          ...mappedAddress,
          address_type_code: (mappedAddress as AddressToSave).address_type_code || address.address_type_code,
          address_id: (address as AddressResponseData).address_id || '',
          skip_usps_validation: (address as Address).skip_usps_validation || false,
        },
        getAddressErrorType(addressError) ?? ErrorType.Invalid,
        isRecipientAddress ? 1 : 0,
        (address as AddressResponseData).address_id,
      );
      hideLoadingScreen(appDispatch);
    },
    [addAddressToValidate, appDispatch, isRecipientAddress],
  );

  // saves the address validating the input and mapping the payload
  // to send the correct data to the API service
  const handleSaveAddress: (addressToSave: AddressForm | Address | AddressResponseData) => Promise<void> = useCallback(
    async (addressToSave) => {
      if (!projectId) {
        // todo: this is just a temporary fix to load the project when the user tries to save an address
        //  this should be removed once we fix it in the AddressView
        await loadExistingProject(lastProjectLoaded || '', isDataLoaded);
        throw new Error('project_id_not_defined');
      }

      setIsAddressLoading(true);

      const mappedAddressToSave = mapAddressToSave(addressToSave);

      return saveAddress(projectId, mappedAddressToSave, IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED)
        .then((response) => {
          if (!response.data) return;

          const mappedAddress = mapAddressesByType([response.data]);
          setAddressData(mappedAddress);
          trackSavedNameOnEnvelope(mappedAddressToSave);
        })
        .catch(async (error: ErrorResponse) => {
          if (error?.status === 422) {
            handleError(error, addressToSave, mappedAddressToSave);
            throw new Error('address_save_failed');
          }

          // 403 on sender POST means the address already exists (e.g., stale state after refresh).
          // Reload project so addressData gets the existing address_id, allowing PUT on retry.
          if (error?.status === 403) {
            await loadExistingProject(projectId, isDataLoaded);
            throw new Error('address_already_exists');
          }

          hideLoadingScreen(appDispatch);
          onSystemError(error);
        })
        .finally(() => {
          setIsAddressLoading(false);
        });
    },
    [
      IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED,
      appDispatch,
      handleError,
      lastProjectLoaded,
      loadExistingProject,
      onSystemError,
      projectId,
      setAddressData,
      trackSavedNameOnEnvelope,
    ],
  );

  // updates the address validating the input and mapping the payload
  // to send the correct data to the API service
  const handleUpdateAddress: (addressToUpdate: Address | AddressResponseData, addressId: string) => Promise<void> =
    useCallback(
      async (addressToUpdate, addressId) => {
        if (!projectId) {
          // todo: this is just a temporary fix to load the project when the user tries to save an address
          //  this should be removed once we fix it in the AddressView
          await loadExistingProject(lastProjectLoaded || '', isDataLoaded);
          throw new Error('project_id_not_defined');
        }

        const mappedAddressToUpdate: AddressRecipientUpdate = {
          first_name: addressToUpdate.first_name,
          last_name: addressToUpdate.last_name,
          display_name: addressToUpdate.display_name,
          company_name: addressToUpdate.company_name,
          address_line_1: addressToUpdate.address_line_1,
          address_line_2: addressToUpdate.address_line_2,
          city: addressToUpdate.city,
          state_code: addressToUpdate.state_code || '',
          zip: addressToUpdate.zip,
          country_code: addressToUpdate.country_code,
          skip_usps_validation: (addressToUpdate as Address).skip_usps_validation,
        };

        setIsAddressLoading(true);

        return updateAddress(
          projectId,
          addressId,
          mappedAddressToUpdate,
          IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED,
        )
          .then((response) => {
            if (!response.data) return;

            const mappedAddress = mapAddressesByType([response.data]);
            setAddressData(mappedAddress);
            trackSavedNameOnEnvelope({
              ...mappedAddressToUpdate,
              address_type_code: addressToUpdate.address_type_code,
            });
          })
          .catch((error: ErrorResponse) => {
            if (error?.status === 422) {
              handleError(error, addressToUpdate, mappedAddressToUpdate);
              throw new Error('address_update_failed');
            }

            hideLoadingScreen(appDispatch);
            onSystemError(error);
          })
          .finally(() => {
            setIsAddressLoading(false);
          });
      },
      [
        IS_ADDRESS_VERIFICATION_ENHANCED_MATCH_ENABLED,
        appDispatch,
        handleError,
        lastProjectLoaded,
        loadExistingProject,
        onSystemError,
        projectId,
        setAddressData,
        trackSavedNameOnEnvelope,
      ],
    );

  // saves the address to the address book
  // validating the input and mapping the payload
  // to send the correct data to the API service
  // using the TSQ client
  const handleSaveToAddressBook: (address: AddressForm) => Promise<void> = useCallback(
    async (address) => {
      try {
        const quickAddressPages = await queryClient.ensureInfiniteQueryData(
          addressOptions.getQuickAddressesInfiniteQuery(),
        );
        const totalContactsStored = quickAddressPages?.pages?.[0]?.paging?.total_items ?? 0;

        const allowedContactsToSave = helperSettingsConfig.limitSaveQuickAddresses - totalContactsStored;
        // Contact limit has been exceeded
        if (allowedContactsToSave <= 0) {
          // This loop is being hit when the user has 0 contacts exactly and tries to add a quick address during the 1:1 flow
          showErrorToast(t('addressView.contactLimitError') || '', t('addressView.contactsErrorTitle') || '');
          return;
        }

        const mappedQuickAddress: QuickAddressSubmitData = {
          first_name: address.first_name,
          last_name: address.last_name,
          display_name: address.display_name,
          company_name: address.company_name,
          address_line_1: address.address_line_1,
          address_line_2: address.address_line_2,
          city: address.city,
          state_code: address.state_code,
          zip: address.zip,
          country_code: address.country_code,
          contact_type_code: 'A',
          skip_address_verification: true,
        };

        await saveAddressToAddressBook.mutateAsync(mappedQuickAddress);
        if (address.isQuickAddress && address.address_type_code === AddressTypeCode.RECIPIENT) {
          trackManuallyAddRecipientQuickAddress();
        }
      } catch {
        // Silently catch — saving to address book is non-critical and must not block the user flow
      }
    },
    [queryClient, saveAddressToAddressBook, showErrorToast, t],
  );

  // onSubmit validates the action to be taken
  // and calls the correct function to save or update the address
  const onSubmit: (address: AddressForm | undefined) => Promise<void> = useCallback(
    async (address) => {
      if (!address) return;

      const addressId = address.address_id;
      if (address && addressId) {
        // updating the sender and recipient addresses
        return handleUpdateAddress(address, addressId);
      } else {
        return handleSaveAddress(address);
      }
    },
    [handleSaveAddress, handleUpdateAddress],
  );

  // handles the submit of the address form
  // takes the AddressForm values and maps the addresses to sender/recipient
  // based on the address_type_code
  // also validates when the user is saving a 1:M (return-only) or 1:1 card
  const handleAddressSubmit: (address: AddressForm) => Promise<void> = useCallback(
    async (address) => {
      if (isDigitalGreeting) {
        return;
      }

      // ---> return-only flow
      const totalSteps = addressTypesToFill.length;
      if (totalSteps === 1) {
        // this should save the address (return/sender only)
        onSubmit(address)
          .then(() => {
            navigateWithParams('/card/customization/preview', envelope);
          })
          .catch(() => {
            openAddressConfirmationDialog();
          });
        return;
      }
      // <--- return-only flow

      // ---> 1:1/sns flow for 1 return and 1 recipient
      if (address.address_type_code === AddressTypeCode.SENDER) {
        trackClickNextToAddressSender();

        // Save the sender address immediately so it persists through page reloads / sign-in redirects.
        // onSubmit will POST (new) or PUT (existing) based on address_id from the /load payload.
        onSubmit(address)
          .then(() => {
            if (isLoggedIn() && address.isQuickAddress) {
              void handleSaveToAddressBook(address);
            }
            setIsRecipientAddress(true);
          })
          .catch((error) => {
            // 403 means sender already exists (stale state). Project was reloaded in handleSaveAddress
            // so addressData now has address_id. User can re-submit and it will use PUT.
            if (error?.message === 'address_already_exists') {
              showErrorToast(t('addressView.pleaseTryAgain') || 'Something went wrong. Please try again.');
              return;
            }
            openAddressConfirmationDialog();
          });

        return;
      }

      // Recipient submission — sender was already saved in the sender step.
      // onSubmit will POST (new) or PUT (existing) based on address_id from the /load payload.
      await onSubmit(address)
        .then(() => {
          if (isLoggedIn() && address.isQuickAddress) {
            void handleSaveToAddressBook(address);
          }
          navigateWithParams('/card/customization/preview', envelope);
        })
        .catch(() => {
          openAddressConfirmationDialog();
        });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      isDigitalGreeting,
      addressTypesToFill.length,
      onSubmit,
      navigateWithParams,
      envelope,
      setIsRecipientAddress,
      trackClickNextToAddressSender,
      handleSaveToAddressBook,
      openAddressConfirmationDialog,
    ],
  );

  // handles the resubmit action when the user wants to skip the address validation
  // and resubmit the address setting custom values to allow the address to be saved
  // for now we set the skip_usps_validation to true only, however, we can add more
  // custom values to the resubmit action
  const handleResubmit = useCallback<HandleAddressResubmit>(
    async ({ senderAddress, recipientAddress }) => {
      closeAddressConfirmationDialog();
      setIsAddressLoading(true);

      try {
        if (senderAddress) {
          await onSubmit(senderAddress);
        }
        if (recipientAddress) {
          await onSubmit(recipientAddress);
        }
      } catch (error: unknown) {
        onSystemError(error as ErrorResponse);
      } finally {
        setIsAddressLoading(false);
      }

      // If only sender was resubmitted in the 1:1 flow (validation dialog at sender step),
      // advance to recipient step. For 1:many or when recipient is included, navigate to preview.
      if (!isOneToMany && senderAddress && !recipientAddress) {
        setIsRecipientAddress(true);
      } else {
        navigateWithParams('/card/customization/preview', envelope);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [closeAddressConfirmationDialog, isOneToMany, navigateWithParams, onSubmit, onSystemError, setIsRecipientAddress],
  );

  return {
    isAddressLoading,
    setIsAddressLoading,
    handleAddressSubmit,
    handleSaveToAddressBook,
    handleResubmit,
  };
};
